import React, { useMemo } from 'react';
import classNames from 'classnames';

import type { RegimeCellMetricsRowDto } from 'api/generated/schemas';

import { formatMetricValue } from '../../results-step/helpers/results-format';
import { EMPTY_VALUE } from '../../results-step/results-step.constants';

import type { ComparisonColumn } from '../hooks/use-regime-comparison';
import type { ComparisonMetric } from '../regime-comparison.constants';
import {
  CURRENT_YEAR_LABEL,
  FUTURE_YEAR_LABEL,
  RANK_COLUMN_STYLE,
  getColumnSizing,
} from '../regime-comparison.constants';

interface RegimeComparisonGridProps {
  /**
   * One entry per award-matrix column, in matrix order and carrying the matrix's own label, so
   * this grid stays aligned with - and reads identically to - the matrix above it.
   */
  columns: ComparisonColumn[];
  metric: ComparisonMetric;
  /** Decided once for every grid in the shared scroll container - see `getColumnSizing`. */
  isFixedColumnWidth?: boolean;
}

const buildCellKey = (columnKey: string, academicRank: number): string =>
  `${columnKey}:${academicRank}`;

const RegimeComparisonGrid = ({
  columns,
  metric,
  isFixedColumnWidth = false,
}: RegimeComparisonGridProps): React.JSX.Element => {
  const column = getColumnSizing(isFixedColumnWidth);

  // The API nests rows under buckets (column-major), but the grid renders rank-major rows, so
  // flatten to a cell lookup once and read every cell from it while rendering.
  const { academicRanks, rowsByCell } = useMemo(() => {
    const ranks = new Set<number>();
    const cells = new Map<string, RegimeCellMetricsRowDto>();

    columns.forEach(({ key, bucket }) => {
      (bucket?.rows ?? []).forEach((row) => {
        if (row.academicRank == null) return;

        ranks.add(row.academicRank);
        cells.set(buildCellKey(key, row.academicRank), row);
      });
    });

    return {
      academicRanks: [...ranks].sort((a, b) => a - b),
      rowsByCell: cells,
    };
  }, [columns]);

  return (
    <div
      className={classNames(
        'border border-navy-100 rounded-md overflow-hidden',
        isFixedColumnWidth ? 'w-max' : 'w-full'
      )}
    >
      <div className="flex">
        <div
          className="shrink-0 flex items-center justify-center px-2 border-b border-navy-100"
          style={RANK_COLUMN_STYLE}
        >
          <span className="text-xs font-semibold text-gray-500 tracking-[1.6px] leading-4">
            Rank
          </span>
        </div>

        <div className="flex flex-1 min-w-0 flex-col border-l border-navy-100">
          <div className="flex">
            {columns.map(({ key, label, bucket }, index) => (
              <div
                key={key}
                className={classNames(
                  'flex items-center justify-center pt-2 pb-1.5 px-2',
                  column.className,
                  { 'border-l border-navy-100': index > 0 }
                )}
                style={column.style}
              >
                <span
                  className={classNames(
                    'text-xs font-semibold tracking-[1.6px] leading-4 text-center truncate',
                    bucket ? 'text-navy-800' : 'text-gray-700'
                  )}
                >
                  {label}
                </span>
              </div>
            ))}
          </div>

          <div className="flex border-b border-navy-100">
            {columns.map(({ key, bucket }, index) => (
              <div
                key={key}
                className={classNames(
                  'flex items-center justify-between py-0.5 text-xs font-semibold text-gray-500 leading-4',
                  column.className,
                  { 'border-l border-navy-100': index > 0 }
                )}
                style={column.style}
              >
                {!!bucket && (
                  <>
                    <span className="flex-1 text-center uppercase">
                      {CURRENT_YEAR_LABEL}
                    </span>
                    <span className="flex-1 text-center uppercase">
                      {FUTURE_YEAR_LABEL}
                    </span>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {academicRanks.map((academicRank, rankIndex) => {
        const isLastRow = rankIndex === academicRanks.length - 1;

        return (
          <div
            key={academicRank}
            className="flex"
            data-testid={`comparison-row-${metric.key}-${academicRank}`}
          >
            <div
              className={classNames(
                'shrink-0 flex items-center justify-center py-2',
                { 'border-b border-navy-100': !isLastRow }
              )}
              style={RANK_COLUMN_STYLE}
            >
              <span className="text-xs font-semibold text-gray-700 leading-4">
                {academicRank}
              </span>
            </div>

            <div
              className={classNames(
                'flex flex-1 min-w-0 border-l border-navy-100',
                { 'border-b border-navy-100': !isLastRow }
              )}
            >
              {columns.map(({ key, bucket }, colIndex) => {
                const row = rowsByCell.get(buildCellKey(key, academicRank));

                return (
                  <div
                    key={key}
                    className={classNames(
                      'flex items-center py-2.5 text-sm leading-4',
                      column.className,
                      bucket ? 'text-gray-700' : 'text-gray-400',
                      { 'border-l border-navy-100': colIndex > 0 }
                    )}
                    style={column.style}
                  >
                    {bucket ? (
                      <>
                        <span className="flex-1 px-1 text-center truncate">
                          {formatMetricValue(
                            row?.currentYear?.[metric.key],
                            metric.kind
                          )}
                        </span>
                        <span className="flex-1 px-1 text-center truncate">
                          {formatMetricValue(
                            row?.futureYear?.[metric.key],
                            metric.kind
                          )}
                        </span>
                      </>
                    ) : (
                      <span className="flex-1 px-1 text-center">
                        {EMPTY_VALUE}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default RegimeComparisonGrid;
